/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { PaymentElement } from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { Button } from "../button/button";
import { useTranslation } from "react-i18next";
import BillingService from "../../utilities/services/billing-service";
import { createLogger } from "../../utilities/logger";

const log = createLogger("SavePaymentMethodForm");

interface SavePaymentMethodFormProps {
    returnUrl: string;
    updateFailed: Function;
    updateSuccessful: Function;
    setOpenEdit: Function;
    isLoading?: boolean;
    setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const COMPONENT_CLASS = "c-save-payment-form";

const SavePaymentMethodForm: React.FC<SavePaymentMethodFormProps> = ({
    updateFailed,
    updateSuccessful,
    returnUrl,
    setOpenEdit,
    isLoading,
    setIsLoading,
}) => {
    const { state } = useAuthState();
    const stripe = useStripe();
    const elements = useElements();
    const [clientSecret, setClientSecret] = useState("");
    const [customerId, setCustomerId] = useState<string>();
    const { t } = useTranslation();

    useEffect(() => {
        const confirmIntent = async () => {
            if (!stripe || !elements || !clientSecret || !customerId) {
                return;
            }

            const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: `${window.location.origin}${returnUrl}`,
                },
                redirect: "if_required",
            });

            if (error) {
                log.error("Stripe confirmSetup error when saving payment method", {
                    exception: error,
                    customerId,
                });
                updateFailed(true);
                setIsLoading && setIsLoading(false);
                return;
            }

            const paymentMethodId =
                typeof setupIntent.payment_method === "string"
                    ? setupIntent.payment_method
                    : undefined;

            if (!paymentMethodId) {
                log.warn("Payment method save failed: no payment method returned from Stripe", {
                    customerId,
                });
                updateFailed(true);
                setIsLoading && setIsLoading(false);
                return;
            }

            try {
                await BillingService.completePaymentMethodSetup({
                    customerId,
                    paymentMethodId,
                });

                log.info("Payment method saved", {
                    customerId,
                });
                updateSuccessful(true);
            } catch (error) {
                log.error("Failed to complete payment method setup", {
                    exception: error,
                    customerId,
                });
                updateFailed(true);
            } finally {
                setIsLoading && setIsLoading(false);
            }
        };

        confirmIntent();
    }, [clientSecret, customerId, elements, returnUrl, stripe, updateFailed, updateSuccessful]);

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        if (!stripe || !elements || !state.user?.email) {
            return;
        }

        const { error: submitError } = await elements.submit();
        if (submitError) {
            log.error("Stripe elements submit error when saving payment method", {
                exception: submitError,
            });
            return;
        }

        setIsLoading && setIsLoading(true);

        try {
            const response = await BillingService.createPaymentMethodSetupIntent({
                email: state.user.email,
            });

            setCustomerId(response.customerId);
            setClientSecret(response.clientSecret);
        } catch (error) {
            log.error("Failed to create payment method setup intent", {
                exception: error,
            });
            updateFailed(true);
            setIsLoading && setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-3/4 md:w-2/3 lg:w-1/2 m-auto">
            <div className={`${COMPONENT_CLASS}__modal-title-container`}>
                <Dialog.Title as="h3" className={`${COMPONENT_CLASS}__modal-title`}>
                    {t("pricing.editPaymentDetails")}
                </Dialog.Title>
            </div>
            <PaymentElement />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "4rem", gap: "50px" }}>
                <Button
                    type="link"
                    buttonText={t("buttons.btn_cancel")}
                    onClick={() => { setOpenEdit(false); }}
                />
                <Button
                    buttonType="submit"
                    buttonText={t("buttons.btn_save")}
                />
            </div>
        </form>
    );
};

export default SavePaymentMethodForm;